(() => {
  const PICKER_SELECTOR = 'variant-picker.variant-picker--playmat';
  const PRODUCT_SELECTOR = '#product';
  const SEAT_COUNT_SELECTOR = '[data-quantity]';
  const ADDON_SELECTOR = '[data-addon-input]';
  const ARMREST_INPUT_SELECTOR = '[name="addon_armrests"][data-addon-input]:checked:enabled';
  const MEDIA_TEMPLATE_SELECTOR = 'template[data-configuration-media]';

  const formatMoney = (cents, format) => {
    const amount = cents / 100;
    const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
    return format.replace(/\{\{\s*amount[^}]*\}\}/, text);
  };

  const getPicker = () => document.querySelector(PICKER_SELECTOR);

  const isConfigurable = (picker) =>
    Boolean(picker?.closest(PRODUCT_SELECTOR)?.querySelector(`${SEAT_COUNT_SELECTOR}, ${ADDON_SELECTOR}`));

  const seatQuantity = (product) =>
    Number(product.dataset.configuredQuantity ?? product.querySelector(`${SEAT_COUNT_SELECTOR}:checked`)?.dataset.quantity ?? 1);

  const nativeModels = (product) => product.dataset.nativeBundles ? JSON.parse(product.dataset.nativeBundles) : null;

  const purchaseQuantity = (product) => nativeModels(product) ? Number(product.dataset.purchaseQuantity || 1) : 1;

  const nativeBundle = (product, quantity) => {
    const models = nativeModels(product);
    if (!models || quantity !== 2) return null;
    return product.querySelector(ARMREST_INPUT_SELECTOR) ? models.arms : models.base;
  };

  const addonInputs = (product, bundled) => Array.from(product.querySelectorAll(`${ADDON_SELECTOR}:checked:enabled`))
    .filter((input) => !bundled || input.name !== 'addon_armrests');

  const addonTotal = (product, quantity) =>
    addonInputs(product, nativeBundle(product, quantity)).reduce((total, input) => {
      const perSeat = input.hasAttribute('data-addon-per-seat') ? quantity : 1;
      return total + Number(input.dataset.addonPrice) * perSeat;
    }, 0);

  const renderPrice = (container, { compare, total, format }) => {
    if (!container) return;
    const onSale = compare > total;
    container.classList.toggle('price--on-sale', onSale);
    container.querySelectorAll('.price__regular .price-item--regular').forEach((el) => (el.textContent = formatMoney(total, format)));
    container.querySelectorAll('.price__sale .price-item--regular').forEach((el) => (el.textContent = formatMoney(compare, format)));
    container.querySelectorAll('.price-item--sale').forEach((el) => (el.textContent = formatMoney(total, format)));
    const discountBadge = container.querySelector('.price__badge-discount');
    if (discountBadge) {
      discountBadge.hidden = !onSale;
      discountBadge.textContent = onSale
        ? discountBadge.dataset.discountFormat.replaceAll('[percent]', String(Math.round((compare - total) * 100 / compare)))
        : '';
    }
    container.closest('.product__price-row')?.querySelectorAll('[data-price-discount-label]').forEach((label) => {
      label.hidden = onSale && discountBadge !== null;
    });
  };

  const boundProducts = new WeakSet();

  const owningForms = (product, picker) => Array.from(product.querySelectorAll('product-form')).filter((element) =>
    element.form.getAttribute('id') === picker.dataset.formId || element.form.getAttribute('id') === `sticky-${picker.dataset.formId}`
  );

  const bindCart = (product, picker) => {
    owningForms(product, picker).forEach((element) => {
      element.prepareConfiguredCart = async (formData) => {
        const quantity = seatQuantity(product);
        const quantityInput = document.forms[picker.dataset.formId]?.elements.namedItem('quantity');
        if (quantityInput && (!quantityInput.checkValidity() || quantity < 1)) {
          throw new Error(picker.dataset.configurationError);
        }
        const bundle = nativeBundle(product, quantity);
        if (product.dataset.nativeQuantityAvailable === 'false' || (bundle && (!bundle.available || !bundle.id))) throw new Error(window.variantStrings.unavailable);
        if (bundle) formData.set('id', bundle.id);
        const purchases = purchaseQuantity(product);
        formData.set('quantity', bundle ? purchases : quantity * purchases);
        if (nativeModels(product)) {
          formData.set('properties[_noa_builder_seats]', String(quantity));
          formData.set('properties[_noa_builder_arms]', product.querySelector(ARMREST_INPUT_SELECTOR) ? '1' : '0');
          formData.set('properties[_noa_builder_liners]', product.querySelector('[name="addon_waterproof"]:checked:enabled') ? '1' : '0');
        }
        const addons = addonInputs(product, bundle).map((input) => ({
          id: Number(input.value),
          quantity: (input.hasAttribute('data-addon-per-seat') ? quantity : 1) * purchases,
          ...(input.dataset.addonProperties && { properties: JSON.parse(input.dataset.addonProperties) }),
        }));
        return addons;
      };
    });
    if (boundProducts.has(product)) return;
    boundProducts.add(product);
    product.addEventListener('submit', (event) => {
      const element = event.target.closest('product-form');
      if (!element?.prepareConfiguredCart) return;
      event.stopImmediatePropagation();
      element.onSubmitHandler(event);
    }, true);
  };

  const syncPrices = (picker) => {
    const product = picker.closest(PRODUCT_SELECTOR);
    const format = picker.dataset.moneyFormat;
    const quantity = seatQuantity(product);
    const unit = Number(picker.dataset.unitPrice);
    const compareUnit = Number(nativeModels(product)?.seat.compareAtPrice ?? picker.dataset.compareAtPrice);
    const bundle = nativeBundle(product, quantity);
    const seatsTotal = bundle ? bundle.price : unit * quantity;
    const seatsCompare = Math.max(bundle ? bundle.compareAtPrice ?? bundle.price : compareUnit * quantity, seatsTotal);
    const addons = addonTotal(product, quantity);
    const purchases = purchaseQuantity(product);
    const totals = { compare: (seatsCompare + addons) * purchases, total: (seatsTotal + addons) * purchases, format };
    renderPrice(product.querySelector('.product__title .price'), totals);
    if (bundle && !bundle.available) {
      product.querySelectorAll('.price-item').forEach((price) => (price.textContent = window.variantStrings.unavailable));
    }
    if (nativeModels(product)) {
      product.querySelectorAll('afterpay-placement').forEach((placement) => placement.setAttribute('data-amount', String(totals.total / 100)));
    }
    product.querySelectorAll('square-placement').forEach((placement) => placement.setAttribute('data-amount', (totals.total / 100).toFixed(2)));
    syncAddonPriceLabels(product, quantity * purchases, format);
  };

  const syncSeatOptionPrices = (product, models, format) => {
    product.querySelectorAll(SEAT_COUNT_SELECTOR).forEach((input) => {
      const quantity = Number(input.dataset.quantity);
      const price = product.querySelector(`[data-size-option="${input.id}"] .size-select__option-price`);
      if (price) price.textContent = formatMoney(nativeBundle(product, quantity)?.price ?? models.seat.price * quantity, format);
    });
  };

  const syncNativeState = (product, picker) => {
    const models = nativeModels(product);
    if (!models) return;
    const quantity = seatQuantity(product);
    const format = picker.dataset.moneyFormat;
    const armrest = product.querySelector('[name="addon_armrests"][data-addon-input]');
    if (armrest) {
      const hidden = quantity === 2 && !models.arms.available;
      armrest.closest('[data-row-key="armrests"]').hidden = hidden;
      armrest.disabled = hidden;
      if (hidden) armrest.checked = false;
      const amount = quantity === 2 ? models.arms.price - models.base.price : Number(armrest.dataset.addonPrice);
      armrest.closest('label').querySelector('.picker-addon__price').textContent = formatMoney(amount * purchaseQuantity(product), format);
    }
    syncSeatOptionPrices(product, models, format);
    const selected = nativeBundle(product, quantity) || models.seat;
    const available = selected.available && product.dataset.nativeQuantityAvailable !== 'false';
    owningForms(product, picker).forEach((element) => {
      const button = element.querySelector('[type="submit"]');
      const label = button?.querySelector('.product-form__add-button-text');
      element.dataset.nativeUnavailable = String(!available);
      if (!button) return;
      if (label && !button.dataset.nativeAvailableLabel) button.dataset.nativeAvailableLabel = label.textContent;
      button.disabled = !available;
      if (available) button.removeAttribute('aria-disabled');
      else button.setAttribute('aria-disabled', 'true');
      if (label) label.textContent = available ? button.dataset.nativeAvailableLabel : window.variantStrings.unavailable;
    });
  };

  const syncAddonPriceLabels = (product, quantity, format) => {
    product.querySelectorAll('[data-addon-price-label]').forEach((label) => {
      const input = label.closest('label')?.querySelector(ADDON_SELECTOR);
      const price = formatMoney(Number(input?.dataset.addonPrice), format);
      label.textContent = quantity > 1
        ? label.dataset.quantityTemplate.replace('[count]', quantity).replace('[price]', price)
        : price;
    });
  };

  const configurationMediaKey = (seats, armrests) => {
    if (seats === 1) return 'one-seat';
    return armrests ? 'two-seats-armrests' : 'default';
  };

  const productMedia = (template, current, slide) => {
    const stashed = template.content.querySelector('[data-configuration="product"]');
    if (stashed || current.dataset.configuration) return stashed;
    const original = current.cloneNode();
    original.dataset.configuration = 'product';
    original.dataset.aspectRatio = slide.querySelector('.product-media-container')?.style.getPropertyValue('--ratio');
    template.content.append(original);
    return original;
  };

  const MEDIA_ENTERING_CLASS = 'product__media-image--entering';

  const currentConfigurationImage = (slide) => {
    const images = slide.querySelectorAll('.product__media img');
    return images[images.length - 1];
  };

  const crossfadeConfigurationMedia = (slide, next) => {
    const current = currentConfigurationImage(slide);
    if (!current || slide.dataset.configuration !== next.dataset.configuration) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      current.replaceWith(next);
      return;
    }
    next.classList.add(MEDIA_ENTERING_CLASS);
    next.addEventListener('animationend', () => {
      next.classList.remove(MEDIA_ENTERING_CLASS);
      current.remove();
    }, { once: true });
    current.after(next);
  };

  const syncConfigurationMedia = (product) => {
    const template = product.querySelector(MEDIA_TEMPLATE_SELECTOR);
    const slide = product.querySelector('.product__media-list > .product__media-item');
    const current = slide && currentConfigurationImage(slide);
    if (!template || !current) return;
    const original = productMedia(template, current, slide);
    const armrests = Boolean(product.querySelector(ARMREST_INPUT_SELECTOR));
    const key = configurationMediaKey(seatQuantity(product), armrests);
    const image =
      template.content.querySelector(`[data-configuration="${key}"]`) ??
      template.content.querySelector('[data-configuration="default"]') ??
      original;
    if (!image) return;
    const target = image.dataset.configuration;
    const shown = current.dataset.configuration ?? 'product';
    if ((slide.dataset.configuration ?? shown) === target) return;
    slide.dataset.configuration = target;
    if (shown === target) return;
    const next = image.cloneNode();
    next.className = current.className;
    next.loading = 'eager';
    if (image.dataset.aspectRatio) {
      slide.querySelector('.product-media-container')?.style.setProperty('--ratio', image.dataset.aspectRatio);
    }
    next.decode().catch(() => {}).then(() => crossfadeConfigurationMedia(slide, next));
    if (!window.matchMedia('(min-width: 750px)').matches) {
      slide.parentElement.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }
  };

  const syncNativePurchaseQuantity = (product, picker, input) => {
    const models = nativeModels(product);
    const seats = seatQuantity(product);
    const limits = [];
    if (models.seat.max != null) limits.push(Math.floor(models.seat.max / seats));
    product.querySelectorAll(`${ADDON_SELECTOR}:checked:enabled`).forEach((addon) => {
      if (addon.dataset.addonMax === undefined || addon.dataset.addonMax === 'null') return;
      const perPurchase = addon.hasAttribute('data-addon-per-seat') ? seats : 1;
      limits.push(Math.floor(Number(addon.dataset.addonMax) / perPurchase));
    });
    const maximum = limits.length ? Math.max(0, Math.min(...limits)) : null;
    const requested = Number(product.dataset.purchaseQuantity);
    let purchases = Number.isFinite(requested) ? Math.max(1, Math.floor(requested)) : 1;
    if (maximum !== null && maximum > 0 && product.dataset.restoredPurchaseQuantity !== 'true') purchases = Math.min(purchases, maximum);
    product.dataset.purchaseQuantity = purchases;
    product.dataset.nativeQuantityAvailable = String(maximum === null || (maximum > 0 && purchases <= maximum));
    if (input) {
      input.min = '1'; input.step = '1'; input.dataset.min = '1';
      if (maximum === null) { input.removeAttribute('max'); delete input.dataset.max; }
      else { input.setAttribute('max', String(Math.max(1, maximum))); input.dataset.max = String(Math.max(1, maximum)); }
      input.value = purchases;
      const controls = input.closest('quantity-input');
      controls?.querySelectorAll('.quantity__button').forEach((button) => {
        const disabled = button.getAttribute('name') === 'minus' ? purchases <= 1 : maximum !== null && purchases >= maximum;
        button.disabled = disabled;
        button.classList.toggle('disabled', disabled);
      });
    }
    const labels = product.querySelectorAll('[data-native-quantity-label]');
    labels.forEach((label) => {
      label.textContent = seats === 2 ? label.dataset.bundleLabel : label.dataset.seatLabel;
    });
    if (input) input.setAttribute('aria-label', seats === 2 ? 'Bundle quantity' : 'Seat quantity');
  };

  const syncQuantity = (picker) => {
    const product = picker.closest(PRODUCT_SELECTOR);
    let quantity = seatQuantity(product);

    const input = document.forms[picker.dataset.formId]?.elements.namedItem('quantity');
    if (nativeModels(product)) syncNativePurchaseQuantity(product, picker, input);
    else if (input) {
      const min = Number(input.min || 1);
      const step = Number(input.step || 1);
      quantity = min + Math.ceil((Math.max(min, quantity) - min) / step) * step;
      if (input.max !== '') quantity = Math.min(quantity, Number(input.max));
      input.value = quantity;
    }
    product.dataset.configuredQuantity = quantity;
    const choices = product.querySelectorAll(SEAT_COUNT_SELECTOR);
    choices.forEach((choice) => {
      choice.checked = Number(choice.dataset.quantity) === quantity;
      if (choice.checked) updateSeatSummary(choice);
    });
    if (choices.length && !product.querySelector(`${SEAT_COUNT_SELECTOR}:checked`)) {
      renderSizeSummary(product, { label: picker.dataset.seatQuantityLabel.replace('[count]', quantity), note: '', icon: '' });
      product.querySelectorAll('[data-row-key="size"] [data-size-option]').forEach((option) => option.setAttribute('aria-selected', 'false'));
    }
  };

  const settleRow = (input) => getPicker()?.settlePlaymatRows(input);

  const renderSizeSummary = (product, { label, note, icon }) => {
    const text = product.querySelector('[data-row-key="size"] [data-size-label]');
    const detail = product.querySelector('[data-row-key="size"] [data-size-note]');
    const media = product.querySelector('[data-row-key="size"] [data-size-icon]');
    if (text) text.textContent = label;
    if (detail) detail.textContent = note;
    if (media) media.innerHTML = icon;
  };

  const updateSeatSummary = (input) => {
    const product = input.closest(PRODUCT_SELECTOR);
    const options = Array.from(product.querySelectorAll('[data-row-key="size"] [data-size-option]'));
    const option = options.find((candidate) => candidate.dataset.sizeOption === input.id);
    if (!option) return;
    options.forEach((candidate) => candidate.setAttribute('aria-selected', String(candidate === option)));
    renderSizeSummary(product, {
      label: option.dataset.label,
      note: option.dataset.note,
      icon: option.querySelector('.size-select__option-icon')?.innerHTML ?? '',
    });
  };

  const ADDON_STEP_SELECTOR = '[data-addon-step]';

  const syncAddonButtons = (product) => {
    product.querySelectorAll('[data-addon-toggle]').forEach((button) => {
      const input = document.getElementById(button.dataset.addonToggle);
      const added = Boolean(input?.checked && !input.disabled);
      button.setAttribute('aria-pressed', String(added));
      const label = button.querySelector('.pdp-addons__button-label');
      if (label) label.textContent = added ? button.dataset.labelAdded : button.dataset.labelAdd;
    });
  };

  let selection = null;

  const rememberSelection = (product) => {
    const stepNames = new Set(Array.from(product.querySelectorAll(ADDON_STEP_SELECTOR), (input) => input.name));
    selection = {
      quantity: seatQuantity(product),
      purchases: purchaseQuantity(product),
      steps: Object.fromEntries(Array.from(stepNames, (name) => [
        name,
        Boolean(product.querySelector(`[name="${name}"][data-addon-input]:checked`)),
      ])),
      addons: Boolean(product.querySelector('[name="addon_id"]:checked')),
    };
  };

  const checkInput = (input, onChecked) => {
    if (!input || input.checked) return;
    input.checked = true;
    onChecked(input);
  };

  const restoreSelection = (product) => {
    if (!selection) return;
    product.dataset.configuredQuantity = selection.quantity;
    if (nativeModels(product)) product.dataset.purchaseQuantity = selection.purchases || 1;
    checkInput(product.querySelector(`${SEAT_COUNT_SELECTOR}[data-quantity="${selection.quantity}"]`), updateSeatSummary);
    Object.entries(selection.steps).forEach(([name, included]) => {
      const input = product.querySelector(`[name="${name}"]${ADDON_STEP_SELECTOR}`);
      if (input) input.checked = included && !input.disabled;
    });
    product.querySelectorAll('[name="addon_id"]').forEach((input) => (input.checked = selection.addons));
  };

  const restoreNativeUrl = (product, url) => {
    if (!nativeModels(product) || !url) return false;
    const params = new URL(url, window.location.href).searchParams;
    const seats = Number(params.get('noa_seats'));
    if (seats !== 1 && seats !== 2) return false;
    product.dataset.configuredQuantity = seats;
    product.dataset.purchaseQuantity = params.get('noa_quantity') || '1';
    product.dataset.restoredPurchaseQuantity = 'true';
    [['addon_armrests', 'noa_arms'], ['addon_waterproof', 'noa_liners']].forEach(([name, key]) => {
      const input = product.querySelector(`[name="${name}"]${ADDON_SELECTOR}`);
      if (input) input.checked = params.get(key) === '1' && !input.disabled;
    });
    return true;
  };

  const syncNativeUrl = (product, writeUrl) => {
    if (!nativeModels(product) || !window.location?.href) return;
    const state = {
      noa_seats: String(seatQuantity(product)),
      noa_quantity: String(purchaseQuantity(product)),
      noa_arms: product.querySelector(ARMREST_INPUT_SELECTOR) ? '1' : '0',
      noa_liners: product.querySelector('[name="addon_waterproof"]:checked:enabled') ? '1' : '0',
    };
    const configuredUrl = (value) => {
      const url = new URL(value, window.location.href);
      Object.entries(state).forEach(([key, value]) => url.searchParams.set(key, value));
      return url;
    };
    product.querySelectorAll('input[data-product-url]').forEach((input) => {
      const url = configuredUrl(input.dataset.productUrl);
      input.dataset.productUrl = url.pathname + url.search;
    });
    if (writeUrl) window.history.replaceState(window.history.state, '', configuredUrl(window.location.href));
  };

  const sync = ({ restore = false, url = null, writeUrl = true } = {}) => {
    const picker = getPicker();
    if (!isConfigurable(picker)) return;
    const product = picker.closest(PRODUCT_SELECTOR);
    if (!restoreNativeUrl(product, url) && restore) restoreSelection(product);
    bindCart(product, picker);
    syncQuantity(picker);
    syncNativeState(product, picker);
    syncConfigurationMedia(product);
    syncPrices(picker);
    syncAddonButtons(product);
    syncNativeUrl(product, writeUrl);
  };

  const handleChange = (event) => {
    const input = event.target;
    const picker = getPicker();
    if (!isConfigurable(picker)) return;
    const product = picker.closest(PRODUCT_SELECTOR);
    if (input.name === 'quantity' && input.form?.getAttribute('id') === picker.dataset.formId) {
      if (nativeModels(product)) {
        delete product.dataset.restoredPurchaseQuantity;
        product.dataset.purchaseQuantity = input.value;
      }
      else product.dataset.configuredQuantity = input.value;
      rememberSelection(product);
      sync();
      return;
    }
    if (!input.matches('[data-local-option]')) return;

    if (input.matches(SEAT_COUNT_SELECTOR)) {
      product.dataset.configuredQuantity = input.dataset.quantity;
      updateSeatSummary(input);
      settleRow(input);
    }
    rememberSelection(input.closest(PRODUCT_SELECTOR));
    sync();
  };

  const handleClick = (event) => {
    const button = event.target.closest('[data-addon-toggle]');
    if (!button) return;
    const input = document.getElementById(button.dataset.addonToggle);
    if (!input) return;
    input.checked = !input.checked;
    rememberSelection(input.closest(PRODUCT_SELECTOR));
    sync();
  };

  const init = () => {
    document.addEventListener('change', handleChange);
    document.addEventListener('click', handleClick);
    document.addEventListener('variant-updated', (event) => sync({ restore: true, url: event.detail?.url, writeUrl: false }));
    subscribe(PUB_SUB_EVENTS.quantityUpdate, () => sync());
    sync({ url: window.location?.href });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
